import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, FileText, Mail, Inbox, LogOut, ExternalLink, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ProductsTable } from "@/components/admin/ProductsTable";
import { BlogTable } from "@/components/admin/BlogTable";
import { SubscribersTable } from "@/components/admin/SubscribersTable";
import { ContactsTable } from "@/components/admin/ContactsTable";
import { ProductFormDialog } from "@/components/admin/ProductFormDialog";
import { BlogFormDialog } from "@/components/admin/BlogFormDialog";

const NAV = [
  { key: "products", label: "Products", icon: Package },
  { key: "blog", label: "Journal", icon: FileText },
  { key: "subscribers", label: "Subscribers", icon: Mail },
  { key: "contacts", label: "Inquiries", icon: Inbox },
];

const EMPTY_PRODUCT = {
  title: "",
  description: "",
  price: "",
  image_url: "",
  amazon_url: "",
  category: "Style",
  featured: false,
};

const EMPTY_BLOG = {
  title: "",
  excerpt: "",
  content: "",
  image_url: "",
  category: "Lifestyle",
  published: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [productDialog, setProductDialog] = useState(false);
  const [blogDialog, setBlogDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [blogForm, setBlogForm] = useState(EMPTY_BLOG);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    const [p, b, s, c] = await Promise.all([
      api.get("/products"),
      api.get("/blog", { params: { include_unpublished: true } }),
      api.get("/newsletter"),
      api.get("/contact"),
    ]);
    setProducts(p.data);
    setPosts(b.data);
    setSubs(s.data);
    setContacts(c.data);
  }, []);

  useEffect(() => {
    loadAll().catch(() => toast.error("Failed to load data"));
  }, [loadAll]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  /* ---------------- Product handlers ---------------- */
  const openNewProduct = () => {
    setEditingProduct(null);
    setProductForm(EMPTY_PRODUCT);
    setProductDialog(true);
  };
  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({
      title: p.title || "",
      description: p.description || "",
      price: p.price || "",
      image_url: p.image_url || "",
      amazon_url: p.amazon_url || "",
      category: p.category || "Style",
      featured: !!p.featured,
    });
    setProductDialog(true);
  };
  const saveProduct = async () => {
    if (!productForm.title || !productForm.image_url || !productForm.amazon_url) {
      toast.error("Title, image URL and Amazon link are required.");
      return;
    }
    setSaving(true);
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, productForm);
        toast.success("Product updated");
      } else {
        await api.post("/products", productForm);
        toast.success("Product added");
      }
      setProductDialog(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Product deleted");
    loadAll();
  };

  /* ---------------- Blog handlers ---------------- */
  const openNewBlog = () => {
    setEditingBlog(null);
    setBlogForm(EMPTY_BLOG);
    setBlogDialog(true);
  };
  const openEditBlog = (b) => {
    setEditingBlog(b);
    setBlogForm({
      title: b.title || "",
      excerpt: b.excerpt || "",
      content: b.content || "",
      image_url: b.image_url || "",
      category: b.category || "Lifestyle",
      published: b.published !== false,
    });
    setBlogDialog(true);
  };
  const saveBlog = async () => {
    if (!blogForm.title || !blogForm.content || !blogForm.image_url) {
      toast.error("Title, content and image URL are required.");
      return;
    }
    setSaving(true);
    try {
      if (editingBlog) {
        await api.put(`/blog/${editingBlog.id}`, blogForm);
        toast.success("Post updated");
      } else {
        await api.post("/blog", blogForm);
        toast.success("Post published");
      }
      setBlogDialog(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const deleteBlog = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/blog/${b.id}`);
    toast.success("Post deleted");
    loadAll();
  };

  /* ---------------- Contact handlers ---------------- */
  const deleteContact = async (c) => {
    if (!window.confirm(`Delete inquiry from ${c.name}?`)) return;
    await api.delete(`/contact/${c.id}`);
    toast.success("Inquiry deleted");
    loadAll();
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen bg-charcoal text-cream flex md:flex-col md:fixed md:inset-y-0">
        <div className="p-6 md:p-8 md:border-b border-cream/15 flex-1 md:flex-none">
          <h1 className="font-display text-2xl leading-none">Emmy</h1>
          <p className="eyebrow text-dustyrose mt-1">Studio</p>
        </div>
        <nav className="flex md:flex-col md:p-4 md:gap-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.key}
                data-testid={`admin-nav-${n.key}`}
                onClick={() => setTab(n.key)}
                className={`flex items-center gap-3 px-5 md:px-4 py-4 md:py-3 font-body text-sm transition-colors ${
                  tab === n.key
                    ? "md:bg-cream/10 text-dustyrose"
                    : "text-cream/70 hover:text-cream"
                }`}
              >
                <Icon size={18} /> <span className="hidden sm:inline">{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="md:mt-auto md:p-4 flex md:flex-col gap-1">
          <button
            data-testid="admin-view-site"
            onClick={() => navigate("/")}
            className="flex items-center gap-3 px-5 md:px-4 py-4 md:py-3 font-body text-sm text-cream/70 hover:text-cream transition-colors"
          >
            <ExternalLink size={18} /> <span className="hidden sm:inline">View Site</span>
          </button>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="flex items-center gap-3 px-5 md:px-4 py-4 md:py-3 font-body text-sm text-cream/70 hover:text-cream transition-colors"
          >
            <LogOut size={18} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 md:ml-64 p-6 md:p-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="font-display text-charcoal text-3xl md:text-4xl leading-none capitalize">
              {tab === "blog" ? "Journal" : tab === "contacts" ? "Inquiries" : tab}
            </h2>
            <p className="font-body text-charcoal/60 text-sm mt-2">
              Welcome back, {user?.name || "Emmy"}.
            </p>
          </div>
          {tab === "products" && (
            <Button data-testid="add-product-button" onClick={openNewProduct} className="bg-maroon hover:bg-burgundy text-cream rounded-none">
              <Plus size={16} className="mr-2" /> Add Product
            </Button>
          )}
          {tab === "blog" && (
            <Button data-testid="add-blog-button" onClick={openNewBlog} className="bg-maroon hover:bg-burgundy text-cream rounded-none">
              <Plus size={16} className="mr-2" /> New Post
            </Button>
          )}
        </div>

        {tab === "products" && (
          <ProductsTable products={products} onEdit={openEditProduct} onDelete={deleteProduct} />
        )}
        {tab === "blog" && (
          <BlogTable posts={posts} onEdit={openEditBlog} onDelete={deleteBlog} />
        )}
        {tab === "subscribers" && <SubscribersTable subs={subs} />}
        {tab === "contacts" && <ContactsTable contacts={contacts} onDelete={deleteContact} />}
      </main>

      <ProductFormDialog
        open={productDialog}
        onOpenChange={setProductDialog}
        form={productForm}
        setForm={setProductForm}
        editing={editingProduct}
        saving={saving}
        onSave={saveProduct}
      />
      <BlogFormDialog
        open={blogDialog}
        onOpenChange={setBlogDialog}
        form={blogForm}
        setForm={setBlogForm}
        editing={editingBlog}
        saving={saving}
        onSave={saveBlog}
      />
    </div>
  );
}
